import { useTheme } from '@/assets/styles/themeMantine';
import { Button, Flex, Text, ActionIcon, Tooltip } from '@mantine/core';
import React, { useCallback, useMemo } from 'react';
import { CheckCircle, Plus } from 'react-feather';
import { useClientTerminology } from '@/views/list/NetworkTerms';
import { LISTS_BASE_PATH } from '@/app/constants';
import { Link } from 'react-router-dom';
import { useGetListsQuery } from '@/features/customListSlice';
import { useMediumScreen, useSmallScreen } from '@/hooks/useWindowDimension';
import TagManager from 'react-gtm-module';
import { GoogleEvents } from '@/features/googleEvents';
import { useDisclosure } from '@mantine/hooks';
import { CreateProjectModal } from './CreateProjectModal';

import classes from './CustomListMapEntry.module.css';
import featureBarClasses from '@/views/feature-bar/FeatureBar.module.css';

const CustomListMapEntry: React.FC = () => {
  const theme = useTheme();
  const isSmallScreen = useSmallScreen();
  const isMediumScreen = useMediumScreen();
  const isCompactVariant = isSmallScreen || isMediumScreen;
  const intl = useClientTerminology();
  const [
    createProjectModalIsOpened,
    { open: openCreateProjectModal, close: closeCreateProjectModal },
  ] = useDisclosure();
  const { data: lists, isLoading } = useGetListsQuery({
    sortBy: 'updated_at',
    sortOrder: 'desc',
  });

  const listButtons = useMemo(() => {
    if (!lists?.length || isCompactVariant) return null;

    const listItems = lists.slice(0, 2).map((list) => (
      <Tooltip
        label={list.name}
        key={list.id}
        openDelay={500}
        withArrow
        color="gray"
      >
        <Button
          component={Link}
          className={classes.button}
          visibleFrom="lg"
          size="sm"
          variant="outline"
          data-testid={`custom-list-button-${list.id}`}
          to={`${LISTS_BASE_PATH}/${list.id}`}
          onClick={() => {
            TagManager.dataLayer({
              dataLayer: {
                event: GoogleEvents.projects.entry.directShortcut,
              },
            });
          }}
        >
          <Text truncate>{list.name}</Text>
        </Button>
      </Tooltip>
    ));
    return listItems;
  }, [lists, isCompactVariant]);

  const handleCreateListClick = useCallback(() => {
    TagManager.dataLayer({
      dataLayer: {
        event: GoogleEvents.projects.creation.clickedCreateProjectFromEntry,
      },
    });

    openCreateProjectModal();
  }, [openCreateProjectModal]);

  const desktopList = useMemo(() => {
    if (isLoading || !lists || isCompactVariant) {
      return null;
    }
    if (lists.length === 0) {
      return (
        <Button
          variant="outline"
          color="tealBrand"
          data-testid="custom-list-create-button"
          className={`${classes.button} ${classes.createButton}`}
          leftSection={<Plus size={16} />}
          ml="md"
          px="xs"
          radius="md"
          onClick={handleCreateListClick}
        >
          {intl.formatMessage({ id: 'customList.createList' })}
        </Button>
      );
    } else if (lists.length >= 1) {
      return (
        <>
          <ActionIcon
            variant="outline"
            className={classes.iconButton}
            data-testid="custom-list-create-button"
            ml="md"
            onClick={handleCreateListClick}
          >
            <Plus size={16} />
          </ActionIcon>
          {listButtons}
          <Button
            component={Link}
            to={LISTS_BASE_PATH}
            variant="outline"
            size="sm"
            className={`${classes.button} ${classes.seeAllButton}`}
            data-testid="custom-list-see-all-button"
            onClick={() => {
              TagManager.dataLayer({
                dataLayer: {
                  event: GoogleEvents.projects.entry.allProjects,
                },
              });
            }}
          >
            {intl.formatMessage({ id: 'customList.seeAll' })}
          </Button>
        </>
      );
    }
  }, [
    isLoading,
    lists,
    isCompactVariant,
    handleCreateListClick,
    intl,
    listButtons,
  ]);

  return (
    <>
      {isCompactVariant ? (
        <Flex
          className={classes.wrapper}
          data-testid="custom-list-mobile-wrapper"
        >
          <Button
            component={Link}
            to={LISTS_BASE_PATH}
            onClick={() => {
              TagManager.dataLayer({
                dataLayer: {
                  event: GoogleEvents.projects.entry.allProjects,
                },
              });
            }}
            className={`${featureBarClasses.button} ${classes.mobileListsButton}`}
            data-testid="custom-list-mobile-button"
            size="sm"
            leftSection={<CheckCircle size={16} />}
          >
            {intl.formatMessage({
              id: 'customList.lists',
            })}
          </Button>
        </Flex>
      ) : (
        <Flex
          bg={theme.other.mainColor.background.primary}
          px={theme.spacing.xs}
          align="center"
          gap="xxs"
          data-testid="custom-list-map-entry"
          className={classes.root}
        >
          <Flex gap="xs" align="center" pl="xxs">
            <CheckCircle size={16} />
            <Text c={theme.other.mainColor.foreground.primary} fw={500} fz="sm">
              {intl.formatMessage({
                id: 'customList.lists',
              })}
            </Text>
          </Flex>
          {!isLoading && (
            <Flex align="center" gap="xxs" className={classes.wrapper}>
              {desktopList}
            </Flex>
          )}
        </Flex>
      )}
      <CreateProjectModal
        opened={createProjectModalIsOpened}
        onClose={closeCreateProjectModal}
        analyticsEvent={GoogleEvents.projects.creation.clickedCreateNextStep}
        submitButtonText={intl.formatMessage({ id: 'action.next' })}
        stepInfo={{
          onStep: 1,
          ofStep: 2,
        }}
      />
    </>
  );
};

export default CustomListMapEntry;
